import type { Venue } from '../types';

type SearchableVenue = Pick<Venue, 'name' | 'type' | 'address'>;

/**
 * Venue types that programme concerts under a name that says nothing about music — a town, an
 * association, a comité des fêtes, a "Salle des Fêtes de X". Searching their bare name returns
 * the town hall's opening hours, so the query says `concerts`.
 */
const NO_MUSIC_IN_THE_NAME: ReadonlySet<NonNullable<Venue['type']>> = new Set([
  'city',
  'association',
  'comite_des_fetes',
  'city_concert_hall',
]);

/**
 * `name`, with the venue's city appended — venue names repeat across towns ("Le Rex",
 * "L'Entrepôt", "Espace Culturel"), so every query needs the town to land in the right one.
 * A `city` venue is named after the town itself (see the city-venue naming convention), so
 * there the city is already the name and nothing is appended.
 */
function withCity(name: string, venue: SearchableVenue): string {
  const city = venue.address?.city?.trim();
  if (!city || city.toLowerCase() === name.toLowerCase()) return name;
  return `${name} ${city}`;
}

/**
 * The Google query that goes looking for a venue's booking address.
 *
 * A `city` venue is named after the town itself, so the contact to find there is the mairie's
 * cultural department. Everything else — hall, festival, bar, association — has a programmer.
 */
export function venueSearchQuery(venue: SearchableVenue): string {
  const name = venue.name?.trim();
  if (!name) return '';
  if (venue.type === 'city') return `contact service culturel ${name}`;
  return `contact email programmateur ${withCity(name, venue)}`;
}

/**
 * The Google query that asks whether the venue has stopped: a festival ends ("fini"), a place
 * closes ("fermé").
 *
 * Empty for a `city` venue — a town does not close, so the caller shows no button there.
 */
export function venueClosedQuery(venue: SearchableVenue): string {
  const name = venue.name?.trim();
  if (!name || venue.type === 'city') return '';
  const verdict = venue.type === 'festival' ? 'fini' : 'fermé';
  return `${withCity(name, venue)} ${verdict}`;
}

/** The Google query for the venue itself — see {@link NO_MUSIC_IN_THE_NAME} for the `concerts` hint. */
export function venueGeneralQuery(venue: SearchableVenue): string {
  const name = venue.name?.trim();
  if (!name) return '';
  const base = withCity(name, venue);
  return venue.type && NO_MUSIC_IN_THE_NAME.has(venue.type) ? `${base} concerts` : base;
}

/** Google results page for `query`. */
export function googleSearchUrl(query: string): string {
  return `https://www.google.com/search?q=${encodeURIComponent(query)}`;
}
